/**
 * Service de cache pour les données de stock
 * Gère le cache en mémoire avec expiration automatique
 */

class StockCacheService {
  constructor() {
    this.cache = new Map(); // Map<shopId, { data, timestamp }>
    this.CACHE_DURATION = 15 * 60 * 1000; // 15 minutes en millisecondes
  }

  /**
   * Vérifie si les données en cache sont encore valides
   * @param {string} shopId - ID du magasin
   * @returns {boolean} true si le cache est valide
   */
  isValid(shopId) {
    const cached = this.cache.get(shopId);
    if (!cached) return false;

    const now = Date.now();
    const age = now - cached.timestamp;
    
    return age < this.CACHE_DURATION;
  }

  /**
   * Récupère les données du cache si elles sont valides
   * @param {string} shopId - ID du magasin
   * @returns {Object|null} Les données en cache ou null
   */
  get(shopId) {
    if (this.isValid(shopId)) {
      return this.cache.get(shopId).data;
    }
    
    // Supprimer le cache expiré
    this.cache.delete(shopId);
    return null;
  }

  /**
   * Stocke les données dans le cache
   * @param {string} shopId - ID du magasin
   * @param {Object} data - Les données à mettre en cache
   */
  set(shopId, data) {
    this.cache.set(shopId, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Supprime le cache pour un shopId spécifique
   * @param {string} shopId - ID du magasin
   */
  clear(shopId) {
    this.cache.delete(shopId);
  }

  /**
   * Supprime tout le cache
   */
  clearAll() {
    this.cache.clear();
  }

  /**
   * Récupère l'âge du cache en minutes
   * @param {string} shopId - ID du magasin
   * @returns {number|null} L'âge en minutes ou null si pas de cache
   */
  getAge(shopId) {
    const cached = this.cache.get(shopId);
    if (!cached) return null;

    const now = Date.now();
    const ageMs = now - cached.timestamp;
    return Math.floor(ageMs / 60000); // Convertir en minutes
  }

  /**
   * Vérifie si le cache doit être rafraîchi
   * @param {string} shopId - ID du magasin
   * @returns {boolean} true si le cache doit être rafraîchi
   */
  needsRefresh(shopId) {
    return !this.isValid(shopId);
  }
}

// Instance singleton
export const stockCache = new StockCacheService();

export default stockCache;

